// src/schema-validator.ts
var VALID_FIELD_TYPES = /* @__PURE__ */ new Set([
  "text",
  "email",
  "tel",
  "number",
  "date",
  "select",
  "checkbox",
  "radio",
  "textarea"
]);
var TYPES_REQUIRING_OPTIONS = /* @__PURE__ */ new Set(["select", "radio"]);
var ConfigError = class extends Error {
  /** Always `'SCHEMA_INVALID'` for schema validation failures. */
  code = "SCHEMA_INVALID";
  constructor(message) {
    super(message);
    this.name = "VoiceFormConfigError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
function validateSchema(schema) {
  if (schema === null || schema === void 0) {
    throw new ConfigError(
      'Schema must be a FormSchema object with a non-empty "fields" array; received null/undefined.'
    );
  }
  if (typeof schema !== "object" || Array.isArray(schema)) {
    throw new ConfigError(
      `Schema must be a FormSchema object with a "fields" array; received ${Array.isArray(schema) ? "array" : typeof schema}.`
    );
  }
  const raw = schema;
  if (!("fields" in raw) || !Array.isArray(raw["fields"])) {
    throw new ConfigError(
      'Schema must have a "fields" property that is an array of field definitions.'
    );
  }
  const fields = raw["fields"];
  if (fields.length === 0) {
    throw new ConfigError(
      'Schema "fields" array must not be empty. Provide at least one field definition.'
    );
  }
  const seenNames = /* @__PURE__ */ new Set();
  const normalizedFields = [];
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (field === null || typeof field !== "object" || Array.isArray(field)) {
      throw new ConfigError(
        `Field ${i}: expected an object, received ${field === null ? "null" : typeof field}.`
      );
    }
    const f = field;
    if (!("name" in f) || typeof f["name"] !== "string") {
      throw new ConfigError(
        `Field ${i}: missing required "name" property (must be a non-empty string).`
      );
    }
    const name = f["name"];
    if (name.trim() === "") {
      throw new ConfigError(
        `Field ${i}: "name" must not be an empty string.`
      );
    }
    if (seenNames.has(name)) {
      throw new ConfigError(
        `Field ${i}: duplicate field name "${name}". Each field name must be unique within the schema.`
      );
    }
    seenNames.add(name);
    if (!("type" in f) || typeof f["type"] !== "string") {
      throw new ConfigError(
        `Field ${i} ("${name}"): missing required "type" property.`
      );
    }
    const type = f["type"];
    if (!VALID_FIELD_TYPES.has(type)) {
      throw new ConfigError(
        `Field ${i} ("${name}"): invalid type "${type}". Valid types are: ${[...VALID_FIELD_TYPES].join(", ")}.`
      );
    }
    const fieldType = type;
    if (TYPES_REQUIRING_OPTIONS.has(fieldType)) {
      const options = f["options"];
      if (!Array.isArray(options) || options.length === 0) {
        throw new ConfigError(
          `Field ${i} ("${name}"): type "${fieldType}" requires a non-empty "options" array.`
        );
      }
    }
    const label = typeof f["label"] === "string" && f["label"].length > 0 ? f["label"] : name;
    normalizedFields.push({ ...f, label });
  }
  const normalizedSchema = {
    ...raw,
    fields: normalizedFields
  };
  return normalizedSchema;
}

// src/state-machine.ts
function transition(state, event) {
  switch (state.status) {
    case "idle":
      return transitionFromIdle(state, event);
    case "recording":
      return transitionFromRecording(state, event);
    case "processing":
      return transitionFromProcessing(state, event);
    case "confirming":
      return transitionFromConfirming(state, event);
    case "injecting":
      return transitionFromInjecting(state, event);
    case "done":
      return transitionFromDone(state, event);
    case "error":
      return transitionFromError(state, event);
  }
}
function transitionFromIdle(state, event) {
  if (event.type === "START") {
    return { status: "recording", interimTranscript: "" };
  }
  return warnInvalid(state, event);
}
function transitionFromRecording(state, event) {
  switch (event.type) {
    case "STT_INTERIM":
      return { status: "recording", interimTranscript: event.transcript };
    case "STT_FINAL":
      if (event.transcript.trim() === "") {
        return { status: "idle" };
      }
      return { status: "processing", transcript: event.transcript };
    case "STT_ERROR":
      return {
        status: "error",
        error: {
          code: mapSttErrorCode(event.error.code),
          message: event.error.message,
          recoverable: true
        },
        previousStatus: "recording"
      };
    case "CANCEL":
      return { status: "idle" };
    default:
      return warnInvalid(state, event);
  }
}
function transitionFromProcessing(state, event) {
  switch (event.type) {
    case "PARSE_SUCCESS":
      return {
        status: "confirming",
        transcript: state.transcript,
        confirmation: event.confirmation
      };
    case "PARSE_ERROR":
      return {
        status: "error",
        error: event.error,
        previousStatus: "processing"
      };
    case "CANCEL":
      return { status: "idle" };
    default:
      return warnInvalid(state, event);
  }
}
function transitionFromConfirming(state, event) {
  switch (event.type) {
    case "CONFIRM":
      return { status: "injecting", confirmation: state.confirmation };
    case "CANCEL":
      return { status: "idle" };
    case "FIELD_CORRECTED":
      return {
        status: "confirming",
        transcript: state.transcript,
        confirmation: event.confirmation
        // new object, not mutated
      };
    default:
      return warnInvalid(state, event);
  }
}
function transitionFromInjecting(state, event) {
  if (event.type === "INJECTION_COMPLETE") {
    return { status: "done", result: event.result };
  }
  return warnInvalid(state, event);
}
function transitionFromDone(state, event) {
  if (event.type === "AUTO_RESET") {
    return { status: "idle" };
  }
  return warnInvalid(state, event);
}
function transitionFromError(state, event) {
  if (event.type === "ACKNOWLEDGE_ERROR" || event.type === "AUTO_RESET") {
    return { status: "idle" };
  }
  return warnInvalid(state, event);
}
function mapSttErrorCode(code) {
  switch (code) {
    case "NOT_SUPPORTED":
      return "STT_NOT_SUPPORTED";
    case "PERMISSION_DENIED":
      return "PERMISSION_DENIED";
    case "NO_SPEECH":
      return "NO_TRANSCRIPT";
    case "NETWORK_ERROR":
    case "AUDIO_CAPTURE_FAILED":
    case "ABORTED":
    case "UNKNOWN":
    default:
      return "UNKNOWN";
  }
}
function warnInvalid(state, event) {
  if (typeof process === "undefined" || process.env.NODE_ENV !== "production") {
    console.warn(
      `[voice-form] Invalid transition: ${state.status} + ${event.type} \u2014 event ignored.`
    );
  }
  return state;
}
function createStateMachine(initialState = { status: "idle" }) {
  let currentState = initialState;
  let listeners = [];
  let isDispatching = false;
  const eventQueue = [];
  let destroyed = false;
  function processEvent(event) {
    const nextState = transition(currentState, event);
    if (nextState === currentState) {
      return;
    }
    currentState = nextState;
    const snapshot = listeners.slice();
    for (const listener of snapshot) {
      listener(currentState, event);
    }
  }
  return {
    getState() {
      return currentState;
    },
    dispatch(event) {
      if (destroyed) return;
      if (isDispatching) {
        eventQueue.push(event);
        return;
      }
      isDispatching = true;
      try {
        processEvent(event);
        while (eventQueue.length > 0) {
          const queued = eventQueue.shift();
          processEvent(queued);
        }
      } finally {
        isDispatching = false;
      }
    },
    subscribe(listener) {
      listeners.push(listener);
      return () => {
        const index = listeners.indexOf(listener);
        if (index !== -1) {
          listeners.splice(index, 1);
        }
      };
    },
    destroy() {
      destroyed = true;
      listeners = [];
      eventQueue.length = 0;
    }
  };
}

// src/adapters/web-speech.ts
var SpeechAdapterError = class extends Error {
  code;
  originalError;
  constructor(code, message, originalError) {
    super(message);
    this.name = "STTError";
    this.code = code;
    this.originalError = originalError;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
function mapSpeechErrorCode(error) {
  switch (error) {
    case "not-allowed":
    case "service-not-allowed":
      return "PERMISSION_DENIED";
    case "network":
      return "NETWORK_ERROR";
    case "no-speech":
      return "NO_SPEECH";
    case "audio-capture":
      return "AUDIO_CAPTURE_FAILED";
    case "aborted":
      return "ABORTED";
    default:
      return "UNKNOWN";
  }
}
function createWebSpeechAdapter() {
  let recognition = null;
  let finalCalled = false;
  function isSupported() {
    if (typeof window === "undefined") return false;
    const w = window;
    return w.SpeechRecognition != null || w.webkitSpeechRecognition != null;
  }
  async function start(events) {
    const SpeechRecognitionCtor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (SpeechRecognitionCtor == null) {
      const err = new SpeechAdapterError(
        "NOT_SUPPORTED",
        "SpeechRecognition is not available in this browser."
      );
      events.onError(err);
      return;
    }
    finalCalled = false;
    recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = navigator.language;
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result == null) continue;
        if (result.isFinal) {
          finalCalled = true;
          events.onFinal(result[0].transcript.trim());
        } else {
          events.onInterim(result[0].transcript);
        }
      }
    };
    recognition.onerror = (event) => {
      const code = mapSpeechErrorCode(event.error);
      if (code === "ABORTED") {
        return;
      }
      finalCalled = true;
      const err = new SpeechAdapterError(
        code,
        `SpeechRecognition error: ${event.error}`,
        event
      );
      events.onError(err);
    };
    recognition.onend = () => {
      if (!finalCalled) {
        events.onFinal("");
      }
      events.onEnd();
    };
    recognition.start();
  }
  function stop() {
    recognition?.stop();
  }
  function abort() {
    if (recognition == null) return;
    finalCalled = true;
    recognition.abort();
  }
  return { isSupported, start, stop, abort };
}

// src/endpoint-client.ts
var EndpointError = class extends Error {
  /** Machine-readable classification of the failure. */
  code;
  /** HTTP status code when available (set for HTTP_ERROR). */
  httpStatus;
  /** Structured debug context attached to this error. */
  debugInfo;
  constructor(code, message, httpStatus, debugInfo) {
    super(message);
    this.name = "EndpointError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.debugInfo = debugInfo;
  }
};
function validateParseResponse(data) {
  if (typeof data !== "object" || data === null) return false;
  const d = data;
  if (typeof d["fields"] !== "object" || d["fields"] === null) return false;
  for (const [key, val] of Object.entries(d["fields"])) {
    if (typeof key !== "string") return false;
    if (typeof val !== "object" || val === null) return false;
    const v = val;
    if (typeof v["value"] !== "string") return false;
    if ("confidence" in v && typeof v["confidence"] !== "number") return false;
  }
  return true;
}
var OPTION_DEFAULTS = {
  timeoutMs: 1e4,
  retries: 1,
  headers: {}};
function resolveEndpointOptions(options, debug = false) {
  return {
    timeoutMs: options?.timeoutMs ?? OPTION_DEFAULTS.timeoutMs,
    retries: options?.retries ?? OPTION_DEFAULTS.retries,
    headers: options?.headers ?? OPTION_DEFAULTS.headers,
    debug
  };
}
var DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-VoiceForm-Request": "1"
};
var RAW_BODY_MAX_LENGTH = 500;
var RETRY_BACKOFF_MS = 500;
async function readTruncatedBody(response) {
  try {
    const text = await response.text();
    return text.slice(0, RAW_BODY_MAX_LENGTH);
  } catch {
    return "";
  }
}
var EndpointClient = class {
  /**
   * @param url     - The BYOE endpoint URL to POST to.
   * @param options - Fully-resolved endpoint options.
   */
  constructor(url, options) {
    this.url = url;
    this.options = options;
  }
  url;
  options;
  /** The AbortController for the currently in-flight fetch, or null. */
  activeController = null;
  /** Timer ID for the retry backoff setTimeout, or null. */
  retryTimerId = null;
  /** Timer ID for the request timeout setTimeout, or null. */
  timeoutId = null;
  /**
   * Set to true when `abort()` is called so the catch block can distinguish
   * a manual abort from a timeout-triggered abort.
   */
  manuallyAborted = false;
  /**
   * The reject function for the outermost `parse()` promise.
   * Stored so `abort()` can settle the promise during the retry backoff window,
   * when no fetch is in flight but a retry timer is pending. (PERF 2.3)
   */
  pendingReject = null;
  /**
   * Send a ParseRequest to the configured endpoint and return a validated
   * ParseResponse.
   *
   * Retry behaviour:
   * - Retries up to `options.retries` times on 5xx responses or network errors.
   * - Does NOT retry on 4xx responses or shape-validation failures.
   * - Each retry is scheduled via a 500ms backoff timer.
   *
   * @param request - The fully-constructed ParseRequest to send.
   * @throws {EndpointError} with code `ABORTED`                — manual abort
   * @throws {EndpointError} with code `TIMEOUT`                — timeout expired
   * @throws {EndpointError} with code `NETWORK_ERROR`          — fetch threw
   * @throws {EndpointError} with code `HTTP_ERROR`             — non-2xx response
   * @throws {EndpointError} with code `INVALID_JSON`           — body not JSON
   * @throws {EndpointError} with code `INVALID_RESPONSE_SHAPE` — shape mismatch
   */
  async parse(request) {
    return new Promise((resolve, reject) => {
      this.pendingReject = reject;
      this._attempt(request, this.options.retries, resolve, reject);
    });
  }
  /**
   * Attempt the fetch once. On retriable failure, schedule a retry via
   * `retryTimerId` and decrement the remaining retry counter.
   *
   * @param request          - The ParseRequest to send.
   * @param retriesRemaining - Number of retries still allowed after this attempt.
   * @param resolve          - The outermost Promise resolve function.
   * @param reject           - The outermost Promise reject function.
   */
  _attempt(request, retriesRemaining, resolve, reject) {
    const controller = new AbortController();
    this.activeController = controller;
    this.manuallyAborted = false;
    this.timeoutId = setTimeout(() => {
      this.timeoutId = null;
      controller.abort();
    }, this.options.timeoutMs);
    const headers = {
      ...DEFAULT_HEADERS,
      ...this.options.headers
    };
    fetch(this.url, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal: controller.signal
    }).then(async (response) => {
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      this.activeController = null;
      if (!response.ok) {
        const rawBody = this.options.debug ? await readTruncatedBody(response) : void 0;
        const is5xx = response.status >= 500;
        const error = new EndpointError(
          "HTTP_ERROR",
          `Endpoint returned HTTP ${response.status}`,
          response.status,
          {
            httpStatus: response.status,
            ...rawBody !== void 0 ? { rawBody } : {},
            timestamp: Date.now()
          }
        );
        if (is5xx && retriesRemaining > 0) {
          this._scheduleRetry(request, retriesRemaining - 1, resolve, reject);
        } else {
          this.pendingReject = null;
          reject(error);
        }
        return;
      }
      let parsed;
      try {
        parsed = await response.json();
      } catch {
        this.pendingReject = null;
        reject(
          new EndpointError("INVALID_JSON", "Response body is not valid JSON", void 0, {
            timestamp: Date.now()
          })
        );
        return;
      }
      if (!validateParseResponse(parsed)) {
        this.pendingReject = null;
        reject(
          new EndpointError(
            "INVALID_RESPONSE_SHAPE",
            "Response body does not match ParseResponse contract",
            void 0,
            { timestamp: Date.now() }
          )
        );
        return;
      }
      const sanitizedFields = {};
      for (const [key, fieldValue] of Object.entries(parsed.fields)) {
        sanitizedFields[key] = {
          value: fieldValue.value,
          ...fieldValue.confidence !== void 0 ? { confidence: fieldValue.confidence } : {}
        };
      }
      this.pendingReject = null;
      resolve({
        ...parsed,
        fields: sanitizedFields
      });
    }).catch((fetchError) => {
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      this.activeController = null;
      const isAbortError2 = fetchError !== null && typeof fetchError === "object" && "name" in fetchError && fetchError.name === "AbortError" || fetchError instanceof Error && fetchError.name === "AbortError";
      if (isAbortError2) {
        if (this.manuallyAborted) {
          this.pendingReject = null;
          reject(
            new EndpointError("ABORTED", "Request was manually aborted", void 0, {
              timestamp: Date.now()
            })
          );
        } else {
          this.pendingReject = null;
          reject(
            new EndpointError(
              "TIMEOUT",
              `Request timed out after ${this.options.timeoutMs}ms`,
              void 0,
              { timestamp: Date.now() }
            )
          );
        }
        return;
      }
      const networkError = new EndpointError(
        "NETWORK_ERROR",
        fetchError instanceof Error ? fetchError.message : "Network request failed",
        void 0,
        { timestamp: Date.now() }
      );
      if (retriesRemaining > 0) {
        this._scheduleRetry(request, retriesRemaining - 1, resolve, reject);
      } else {
        this.pendingReject = null;
        reject(networkError);
      }
    });
  }
  /**
   * Schedule a retry attempt after a 500ms backoff.
   *
   * The timer ID is stored in `this.retryTimerId` so that `abort()` can
   * cancel it — preventing spurious network requests after a cancel. (PERF 2.3)
   *
   * @param request          - The ParseRequest to retry.
   * @param retriesRemaining - Remaining retries after this scheduled attempt.
   * @param resolve          - The outer Promise resolve.
   * @param reject           - The outer Promise reject.
   */
  _scheduleRetry(request, retriesRemaining, resolve, reject) {
    this.retryTimerId = setTimeout(() => {
      this.retryTimerId = null;
      this._attempt(request, retriesRemaining, resolve, reject);
    }, RETRY_BACKOFF_MS);
  }
  /**
   * Abort the currently in-flight request, any pending retry timer, and any
   * pending timeout timer.
   *
   * After this call:
   * - The in-flight fetch (if any) rejects with an `AbortError`, which is
   *   caught and re-thrown as `EndpointError(ABORTED)`.
   * - If called during the retry backoff window (no fetch in flight), the
   *   pending `parse()` promise is rejected immediately with `ABORTED`.
   * - No retry will fire.
   *
   * Calling this when no request is in flight is a safe no-op. (PERF 2.3, 5.3)
   */
  abort() {
    if (this.retryTimerId !== null) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.activeController !== null) {
      this.manuallyAborted = true;
      this.activeController.abort();
      this.activeController = null;
      return;
    }
    if (this.pendingReject !== null) {
      const reject = this.pendingReject;
      this.pendingReject = null;
      reject(
        new EndpointError("ABORTED", "Request was manually aborted during retry backoff", void 0, {
          timestamp: Date.now()
        })
      );
    }
  }
};

// src/utils/sanitize.ts
var SanitizeError = class extends Error {
  code;
  recoverable;
  constructor(code, message) {
    super(message);
    this.name = "VoiceFormError";
    this.code = code;
    this.recoverable = true;
  }
};
function stripHtml(value) {
  if (!value.includes("<")) return value;
  const doc = new DOMParser().parseFromString(value, "text/html");
  return doc.body.textContent ?? "";
}
function sanitizeFieldValue(value, fieldType, options) {
  if (typeof value === "boolean") {
    return { value, wasModified: false };
  }
  if (Array.isArray(value)) {
    const stripped2 = value.map((element) => stripHtml(element));
    const wasModified = stripped2.some((s, i) => s !== value[i]);
    return { value: stripped2, wasModified };
  }
  const stripped = stripHtml(value);
  switch (fieldType) {
    // ── Number ─────────────────────────────────────────────────────────────
    // Accepts integers and decimals, optionally negative.
    // Rejects: empty string, alphanumeric mix, whitespace, currency symbols.
    case "number": {
      if (!/^-?\d+(\.\d+)?$/.test(stripped)) {
        throw new SanitizeError(
          "INVALID_FIELD_VALUE",
          `LLM returned non-numeric value for number field: "${stripped}"`
        );
      }
      return { value: stripped, wasModified: stripped !== value };
    }
    // ── Date ────────────────────────────────────────────────────────────────
    // Only ISO 8601 short form (YYYY-MM-DD) is accepted.
    // Rejects: human-readable dates, partial dates, different delimiters.
    case "date": {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(stripped)) {
        throw new SanitizeError(
          "INVALID_FIELD_VALUE",
          `LLM returned invalid date format for date field: "${stripped}" (expected YYYY-MM-DD)`
        );
      }
      return { value: stripped, wasModified: stripped !== value };
    }
    // ── Select / Radio ──────────────────────────────────────────────────────
    // The value must appear in the caller-supplied `options` array.
    // Matching is case-insensitive. The CANONICAL casing from the options
    // array is returned, not the LLM-supplied casing.
    //
    // If `options` is undefined or empty, there are no valid values — throw.
    // This prevents a schema misconfiguration from silently accepting anything.
    case "select":
    case "radio": {
      if (!options || options.length === 0) {
        throw new SanitizeError(
          "INVALID_FIELD_VALUE",
          `sanitizeFieldValue: no options provided for ${fieldType} field`
        );
      }
      const strippedLower = stripped.toLowerCase();
      const match = options.find((opt) => opt.toLowerCase() === strippedLower);
      if (match === void 0) {
        throw new SanitizeError(
          "INVALID_FIELD_VALUE",
          `LLM returned value "${stripped}" which is not in the options list for ${fieldType} field`
        );
      }
      const wasModified = stripped !== value || match !== stripped;
      return { value: match, wasModified };
    }
    // ── All other types (text, email, tel, textarea) ─────────────────────────
    // HTML stripping is the only transformation applied.
    default: {
      return { value: stripped, wasModified: stripped !== value };
    }
  }
}
function validateFieldConstraints(value, field) {
  const v = field.validation;
  if (!v) return { valid: true };
  if (v.minLength !== void 0 && value.length < v.minLength) {
    return {
      valid: false,
      reason: `Value length ${value.length} is below minLength ${v.minLength}`
    };
  }
  if (v.maxLength !== void 0 && value.length > v.maxLength) {
    return {
      valid: false,
      reason: `Value length ${value.length} exceeds maxLength ${v.maxLength}`
    };
  }
  if (v.min !== void 0 || v.max !== void 0) {
    const numeric = Number(value);
    if (!isFinite(numeric)) {
      return {
        valid: false,
        reason: `Value "${value}" is not a valid number \u2014 cannot evaluate min/max constraints`
      };
    }
    if (v.min !== void 0 && numeric < v.min) {
      return {
        valid: false,
        reason: `Value ${numeric} is below min ${v.min}`
      };
    }
    if (v.max !== void 0 && numeric > v.max) {
      return {
        valid: false,
        reason: `Value ${numeric} exceeds max ${v.max}`
      };
    }
  }
  if (v.pattern !== void 0 && process.env.NODE_ENV !== "production") {
    console.warn(
      `[voice-form] Pattern validation is not yet implemented due to ReDoS risk. Pattern "${v.pattern}" on field "${field.name}" will be ignored. See docs/SECURITY.md.`
    );
  }
  return { valid: true };
}

// src/injector.ts
var nativeInputSetter = Object.getOwnPropertyDescriptor(
  HTMLInputElement.prototype,
  "value"
)?.set;
var nativeTextAreaSetter = Object.getOwnPropertyDescriptor(
  HTMLTextAreaElement.prototype,
  "value"
)?.set;
var TRUTHY_VALUES = /* @__PURE__ */ new Set(["true", "yes", "1", "on", "checked"]);
function createInjector(config) {
  const elementCache = /* @__PURE__ */ new Map();
  if (config.onFill) {
    const { onFill } = config;
    return {
      inject: (fields) => runCallbackMode(fields, onFill),
      clearCache: () => {
      }
    };
  }
  const root = config.formElement ?? document;
  return {
    inject: (fields) => runDomMode(fields, root, elementCache, config),
    clearCache: () => elementCache.clear()
  };
}
async function runCallbackMode(fields, onFill) {
  const outcomes = {};
  for (const [fieldName, parsed] of Object.entries(fields)) {
    try {
      await onFill(fieldName, parsed.value);
      outcomes[fieldName] = { status: "injected", value: parsed.value };
    } catch (err) {
      outcomes[fieldName] = {
        status: "failed",
        error: errorMessage(err)
      };
    }
  }
  return buildResult(outcomes);
}
function runDomMode(fields, root, elementCache, config) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      const outcomes = {};
      const work = [];
      for (const [fieldName, parsed] of Object.entries(fields)) {
        const el = resolveElement(fieldName, root, elementCache);
        if (el === null) {
          if (config.multiStep) {
            console.warn(
              `[voice-form] Field "${fieldName}" not found in DOM \u2014 expected in multiStep mode.`
            );
          } else {
            console.error(
              `[voice-form] Field "${fieldName}" not found in DOM.`
            );
          }
          work.push({
            name: fieldName,
            plan: { kind: "skip", reason: { status: "skipped", reason: "element-not-found" } }
          });
          continue;
        }
        if (el.hasAttribute("disabled")) {
          work.push({
            name: fieldName,
            plan: { kind: "skip", reason: { status: "skipped", reason: "disabled" } }
          });
          continue;
        }
        if (el.hasAttribute("readonly")) {
          work.push({
            name: fieldName,
            plan: { kind: "skip", reason: { status: "skipped", reason: "read-only" } }
          });
          continue;
        }
        const fieldType = detectFieldType(el);
        const sanitizeType = fieldType === "select" || fieldType === "radio" ? "text" : fieldType;
        let sanitizedValue;
        try {
          const result = sanitizeFieldValue(parsed.value, sanitizeType);
          sanitizedValue = typeof result.value === "string" ? result.value : String(result.value);
        } catch {
          outcomes[fieldName] = { status: "failed", error: "INVALID_FIELD_VALUE" };
          continue;
        }
        if (el instanceof HTMLSelectElement) {
          let matchIndex = -1;
          const optionCount = el.options.length;
          for (let i = 0; i < optionCount; i++) {
            if (el.options[i].value === sanitizedValue) {
              matchIndex = i;
              break;
            }
          }
          if (optionCount > 0 && matchIndex === -1) {
            work.push({
              name: fieldName,
              plan: { kind: "skip", reason: { status: "skipped", reason: "value-not-in-options" } }
            });
            continue;
          }
          work.push({ name: fieldName, plan: { kind: "select", el, value: sanitizedValue } });
        } else if (el instanceof HTMLInputElement && el.type === "checkbox") {
          work.push({
            name: fieldName,
            plan: {
              kind: "checkbox",
              el,
              checked: TRUTHY_VALUES.has(sanitizedValue.toLowerCase())
            }
          });
        } else if (el instanceof HTMLInputElement && el.type === "radio") {
          const escaped = CSS.escape(fieldName);
          const allRadios = Array.from(
            root.querySelectorAll(`[name="${escaped}"]`)
          ).filter((r) => r instanceof HTMLInputElement && r.type === "radio");
          work.push({
            name: fieldName,
            plan: { kind: "radio", allRadios, targetValue: sanitizedValue }
          });
        } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          const isAppendableType = fieldType === "text" || fieldType === "textarea";
          let finalValue = sanitizedValue;
          if (config.appendMode && isAppendableType && typeof parsed.existingValue === "string" && parsed.existingValue.trim() !== "") {
            finalValue = parsed.existingValue + " " + sanitizedValue;
          }
          work.push({ name: fieldName, plan: { kind: "text", el, value: finalValue } });
        } else {
          outcomes[fieldName] = { status: "failed", error: "Unsupported element type" };
          continue;
        }
      }
      for (const { plan } of work) {
        switch (plan.kind) {
          case "skip":
            break;
          case "text":
            setNativeValue(plan.el, plan.value);
            break;
          case "select":
            plan.el.value = plan.value;
            break;
          case "checkbox":
            plan.el.checked = plan.checked;
            break;
          case "radio": {
            for (const radio of plan.allRadios) {
              radio.checked = radio.value === plan.targetValue;
            }
            break;
          }
        }
      }
      for (const { name: fieldName, plan } of work) {
        switch (plan.kind) {
          case "skip":
            outcomes[fieldName] = plan.reason;
            break;
          case "text": {
            plan.el.dispatchEvent(new Event("input", { bubbles: true }));
            plan.el.dispatchEvent(new Event("change", { bubbles: true }));
            outcomes[fieldName] = { status: "injected", value: plan.value };
            break;
          }
          case "select":
            plan.el.dispatchEvent(new Event("change", { bubbles: true }));
            outcomes[fieldName] = { status: "injected", value: plan.value };
            break;
          case "checkbox":
            plan.el.dispatchEvent(new Event("change", { bubbles: true }));
            outcomes[fieldName] = {
              status: "injected",
              value: String(plan.checked)
            };
            break;
          case "radio": {
            const target = plan.allRadios.find((r) => r.value === plan.targetValue);
            if (target) {
              target.dispatchEvent(new Event("change", { bubbles: true }));
              outcomes[fieldName] = { status: "injected", value: plan.targetValue };
            } else {
              outcomes[fieldName] = { status: "skipped", reason: "element-not-found" };
            }
            break;
          }
        }
      }
      resolve(buildResult(outcomes, config.multiStep ?? false));
    });
  });
}
function resolveElement(fieldName, root, cache) {
  if (cache.has(fieldName)) {
    return cache.get(fieldName) ?? null;
  }
  const escaped = CSS.escape(fieldName);
  const el = root.querySelector(`[name="${escaped}"]`) ?? root.querySelector(`#${escaped}`) ?? root.querySelector(`[data-voiceform="${escaped}"]`);
  cache.set(fieldName, el);
  return el;
}
function setNativeValue(element, value) {
  const setter = element instanceof HTMLTextAreaElement ? nativeTextAreaSetter : nativeInputSetter;
  setter?.call(element, value);
}
function detectFieldType(el) {
  if (el instanceof HTMLTextAreaElement) return "textarea";
  if (el instanceof HTMLSelectElement) return "select";
  if (el instanceof HTMLInputElement) {
    switch (el.type) {
      case "email":
        return "email";
      case "tel":
        return "tel";
      case "number":
        return "number";
      case "date":
        return "date";
      case "checkbox":
        return "checkbox";
      case "radio":
        return "radio";
      default:
        return "text";
    }
  }
  return "text";
}
function buildResult(outcomes, multiStep = false) {
  if (multiStep) {
    const nonSkippedNotFound = Object.values(outcomes).filter(
      (o) => !(o.status === "skipped" && o.reason === "element-not-found")
    );
    const success2 = nonSkippedNotFound.length === 0 || nonSkippedNotFound.every((o) => o.status === "injected");
    return { success: success2, fields: outcomes };
  }
  const success = Object.values(outcomes).every((o) => o.status === "injected");
  return { success, fields: outcomes };
}
function errorMessage(err) {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return String(err);
}

// src/utils/validate-transcript.ts
var CONTROL_CHAR_PATTERN = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/;
var DEFAULT_MAX_LENGTH = 2e3;
function validateTranscript(transcript, maxLength = DEFAULT_MAX_LENGTH) {
  if (transcript.trim().length === 0) {
    return {
      valid: false,
      code: "INVALID_TRANSCRIPT",
      message: "Transcript is empty"
    };
  }
  if (transcript.length > maxLength) {
    return {
      valid: false,
      code: "TRANSCRIPT_TOO_LONG",
      message: `Transcript exceeds maximum length of ${maxLength} characters`
    };
  }
  if (CONTROL_CHAR_PATTERN.test(transcript)) {
    return {
      valid: false,
      code: "INVALID_TRANSCRIPT",
      message: "Transcript contains invalid characters"
    };
  }
  return { valid: true, transcript: transcript.trim() };
}

// src/create-voice-form.ts
var VoiceFormConfigError = class extends Error {
  code;
  constructor(code, message) {
    super(message);
    this.name = "VoiceFormConfigError";
    this.code = code;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var VoiceFormErrorImpl = class extends Error {
  code;
  recoverable;
  constructor(code, message, recoverable = true, debugInfo) {
    super(message);
    this.name = "VoiceFormError";
    this.code = code;
    this.recoverable = recoverable;
    if (debugInfo !== void 0) {
      Object.defineProperty(this, "debugInfo", {
        value: debugInfo,
        writable: false,
        enumerable: true,
        configurable: false
      });
    }
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
function safeInvokeCallback(callback, arg) {
  if (!callback) return void 0;
  try {
    return callback(arg);
  } catch (err) {
    console.error("[voice-form] Error in developer callback:", err);
    return void 0;
  }
}
function invokeBeforeConfirm(callback, arg, onError) {
  if (!callback) return arg;
  try {
    const result = callback(arg);
    return result !== void 0 ? result : arg;
  } catch (err) {
    console.error("[voice-form] Error in onBeforeConfirm callback:", err);
    if (onError) {
      safeInvokeCallback(onError, new VoiceFormErrorImpl(
        "BEFORE_CONFIRM_FAILED",
        `onBeforeConfirm callback threw an exception \u2014 using original confirmation data as fallback. Original error: ${err instanceof Error ? err.message : String(err)}`,
        true
      ));
    }
    return arg;
  }
}
function isAbortError(err) {
  if (err === null || typeof err !== "object") return false;
  const e = err;
  if (e["name"] === "AbortError") return true;
  if (e["code"] === "ABORTED") return true;
  return false;
}
function normalizeError(err) {
  if (err !== null && typeof err === "object") {
    const e = err;
    if (typeof e["code"] === "string") {
      const code = e["code"];
      if (code === "TIMEOUT") {
        return new VoiceFormErrorImpl("ENDPOINT_TIMEOUT", String(e["message"] ?? "Request timed out"), true);
      }
      if (code === "NETWORK_ERROR" || code === "HTTP_ERROR") {
        return new VoiceFormErrorImpl("ENDPOINT_ERROR", String(e["message"] ?? "Endpoint request failed"), true);
      }
      if (code === "INVALID_JSON" || code === "INVALID_RESPONSE_SHAPE") {
        return new VoiceFormErrorImpl("INVALID_RESPONSE", String(e["message"] ?? "Invalid response from endpoint"), true);
      }
    }
  }
  return new VoiceFormErrorImpl(
    "ENDPOINT_ERROR",
    err instanceof Error ? err.message : "An unknown error occurred",
    true
  );
}
function resolveElementForRead(fieldName, root) {
  const escaped = CSS.escape(fieldName);
  const el = root.querySelector(`[name="${escaped}"]`) ?? root.querySelector(`#${escaped}`) ?? root.querySelector(`[data-voiceform="${escaped}"]`);
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return el;
  }
  return null;
}
function buildConfirmationData(response, transcript, schema, options = { appendMode: false }) {
  const parsedFields = {};
  const missingFields = [];
  const invalidFields = [];
  for (const fieldDef of schema.fields) {
    const raw = response.fields[fieldDef.name];
    if (raw === void 0) {
      missingFields.push(fieldDef.name);
      continue;
    }
    const label = fieldDef.label ?? fieldDef.name;
    let sanitizedValue = raw.value;
    let constraintViolation = false;
    try {
      const result = sanitizeFieldValue(
        raw.value,
        fieldDef.type,
        fieldDef.options
      );
      sanitizedValue = typeof result.value === "string" ? result.value : String(result.value);
      const constraintResult = validateFieldConstraints(sanitizedValue, fieldDef);
      if (!constraintResult.valid) {
        constraintViolation = true;
        invalidFields.push({
          name: fieldDef.name,
          value: sanitizedValue,
          reason: constraintResult.reason ?? "Constraint validation failed"
        });
      }
    } catch (err) {
      sanitizedValue = raw.value;
      constraintViolation = true;
      invalidFields.push({
        name: fieldDef.name,
        value: raw.value,
        reason: err instanceof Error ? err.message : "Sanitization failed"
      });
    }
    parsedFields[fieldDef.name] = {
      label,
      value: sanitizedValue,
      ...raw.confidence !== void 0 ? { confidence: raw.confidence } : {}
    };
    if (options.appendMode && options.formElement !== void 0 && (fieldDef.type === "text" || fieldDef.type === "textarea")) {
      const el = resolveElementForRead(fieldDef.name, options.formElement);
      if (el !== null && typeof el.value === "string" && el.value.trim() !== "") {
        parsedFields[fieldDef.name] = {
          ...parsedFields[fieldDef.name],
          existingValue: el.value
        };
      }
    }
  }
  return {
    transcript,
    parsedFields,
    missingFields,
    invalidFields,
    appendMode: options.appendMode
  };
}
function sanitizeConfirmationData(data, schema) {
  const sanitizedParsedFields = {};
  const fieldsByName = new Map(schema.fields.map((f) => [f.name, f]));
  for (const [fieldName, confirmedField] of Object.entries(data.parsedFields)) {
    const fieldDef = fieldsByName.get(fieldName);
    const fieldType = fieldDef?.type ?? "text";
    const options = fieldDef?.options;
    let sanitizedValue = confirmedField.value;
    try {
      const result = sanitizeFieldValue(confirmedField.value, fieldType, options);
      sanitizedValue = typeof result.value === "string" ? result.value : String(result.value);
    } catch {
    }
    sanitizedParsedFields[fieldName] = {
      ...confirmedField,
      value: sanitizedValue
    };
  }
  return {
    ...data,
    parsedFields: sanitizedParsedFields
  };
}
function createVoiceForm(config) {
  let currentSchema;
  try {
    currentSchema = validateSchema(config.schema);
  } catch (err) {
    throw err;
  }
  if (!config.endpoint || config.endpoint.trim() === "") {
    throw new VoiceFormConfigError(
      "INIT_FAILED",
      "VoiceFormConfig.endpoint is required. Provide a URL for your BYOE parse endpoint."
    );
  }
  const sttAdapter = config.sttAdapter ?? createWebSpeechAdapter();
  const resolvedOptions = resolveEndpointOptions(config.endpointOptions, config.debug ?? false);
  const endpointClient = new EndpointClient(config.endpoint, resolvedOptions);
  let formElementResolved;
  if (typeof config.formElement === "string") {
    let found;
    try {
      found = document.querySelector(config.formElement);
    } catch {
      throw new VoiceFormConfigError(
        "INIT_FAILED",
        `VoiceFormConfig.formElement: invalid CSS selector "${config.formElement}". Provide a valid selector or an HTMLElement reference.`
      );
    }
    if (found === null) {
      throw new VoiceFormConfigError(
        "INIT_FAILED",
        `VoiceFormConfig.formElement: selector "${config.formElement}" matched no element. Ensure the element exists before calling createVoiceForm().`
      );
    }
    formElementResolved = found;
  } else {
    formElementResolved = config.formElement;
  }
  let injector = createInjector({
    ...formElementResolved !== void 0 ? { formElement: formElementResolved } : {},
    appendMode: config.appendMode ?? false,
    multiStep: config.multiStep ?? false
  });
  const machine = createStateMachine();
  let destroyed = false;
  let autoResetTimer = null;
  let _lastRequestTimestamp = 0;
  const cooldownMs = config.requestCooldownMs ?? 3e3;
  let cooldownActive = false;
  let cooldownTimerId = null;
  function startCooldownTimer() {
    if (cooldownMs <= 0) return;
    if (cooldownTimerId !== null) clearTimeout(cooldownTimerId);
    cooldownActive = true;
    cooldownTimerId = setTimeout(() => {
      cooldownTimerId = null;
      cooldownActive = false;
    }, cooldownMs);
  }
  let sttEventsActive = false;
  let handlingTransition = false;
  function handleDone(state) {
    safeInvokeCallback(config.events?.onDone, state.result);
    if (autoResetTimer !== null) clearTimeout(autoResetTimer);
    autoResetTimer = setTimeout(() => {
      autoResetTimer = null;
      if (!destroyed) machine.dispatch({ type: "AUTO_RESET" });
    }, 500);
    startCooldownTimer();
  }
  function handleError(state) {
    safeInvokeCallback(config.events?.onError, state.error);
    if (state.error.recoverable) {
      if (autoResetTimer !== null) clearTimeout(autoResetTimer);
      autoResetTimer = setTimeout(() => {
        autoResetTimer = null;
        if (!destroyed) machine.dispatch({ type: "AUTO_RESET" });
      }, 3e3);
    }
  }
  function handleIdle(event) {
    if (event.type === "CANCEL" && config.events?.onCancel) {
      try {
        config.events.onCancel();
      } catch (err) {
        console.error("[voice-form] Error in onCancel callback:", err);
      }
    }
  }
  async function handleStateTransition(state, event) {
    safeInvokeCallback(config.events?.onStateChange, state);
    switch (state.status) {
      case "recording":
        break;
      case "processing": {
        const maxLength = config.maxTranscriptLength ?? 2e3;
        const validation = validateTranscript(state.transcript, maxLength);
        if (!validation.valid) {
          handlingTransition = false;
          machine.dispatch({
            type: "PARSE_ERROR",
            error: new VoiceFormErrorImpl(validation.code, validation.message, true)
          });
          return;
        }
        let dispatchedFollowUp = false;
        try {
          const requestId = crypto.randomUUID();
          const request = {
            transcript: validation.transcript,
            schema: currentSchema,
            requestId
          };
          const response = await endpointClient.parse(request);
          _lastRequestTimestamp = Date.now();
          const confirmation = buildConfirmationData(response, validation.transcript, currentSchema, {
            appendMode: config.appendMode ?? false,
            ...formElementResolved !== void 0 ? { formElement: formElementResolved } : {}
          });
          const maybeModified = invokeBeforeConfirm(
            config.events?.onBeforeConfirm,
            confirmation,
            config.events?.onError
          );
          const sanitized = sanitizeConfirmationData(maybeModified, currentSchema);
          handlingTransition = false;
          dispatchedFollowUp = true;
          machine.dispatch({ type: "PARSE_SUCCESS", response, confirmation: sanitized });
        } catch (err) {
          if (isAbortError(err)) {
            return;
          }
          if (!dispatchedFollowUp) {
            handlingTransition = false;
            machine.dispatch({ type: "PARSE_ERROR", error: normalizeError(err) });
          }
        }
        break;
      }
      case "confirming":
        break;
      case "injecting": {
        let injectionResult;
        try {
          injectionResult = await injector.inject(state.confirmation.parsedFields);
        } catch {
          injectionResult = { success: false, fields: {} };
        }
        handlingTransition = false;
        machine.dispatch({ type: "INJECTION_COMPLETE", result: injectionResult });
        break;
      }
      // Synchronous states — handle inline, no async work needed.
      case "done":
        handleDone(state);
        break;
      case "error":
        handleError(state);
        break;
      case "idle":
        handleIdle(event);
        break;
    }
  }
  machine.subscribe((state, event) => {
    if (handlingTransition) return;
    handlingTransition = true;
    handleStateTransition(state, event).finally(() => {
      handlingTransition = false;
    });
  });
  const sttEvents = {
    onInterim(transcript) {
      if (!sttEventsActive) return;
      machine.dispatch({ type: "STT_INTERIM", transcript });
      safeInvokeCallback(config.events?.onInterimTranscript, transcript);
    },
    onFinal(transcript) {
      if (!sttEventsActive) return;
      machine.dispatch({ type: "STT_FINAL", transcript });
    },
    onError(error) {
      if (!sttEventsActive) return;
      machine.dispatch({ type: "STT_ERROR", error });
    },
    onEnd() {
      sttEventsActive = false;
    }
  };
  return {
    getState() {
      return machine.getState();
    },
    getParsedFields() {
      const state = machine.getState();
      switch (state.status) {
        case "confirming":
          return state.confirmation.parsedFields;
        case "injecting":
          return state.confirmation.parsedFields;
        case "done": {
          return null;
        }
        default:
          return null;
      }
    },
    async start() {
      if (destroyed) return;
      const currentState = machine.getState();
      if (currentState.status !== "idle") return;
      if (cooldownActive) {
        safeInvokeCallback(
          config.events?.onError,
          new VoiceFormErrorImpl(
            "COOLDOWN_ACTIVE",
            "Request cooldown is active. Please wait before trying again.",
            true
          )
        );
        return;
      }
      machine.dispatch({ type: "START" });
      if (machine.getState().status !== "recording") return;
      sttEventsActive = true;
      try {
        await sttAdapter.start(sttEvents);
      } catch (err) {
        sttEventsActive = false;
        const sttErr = err;
        machine.dispatch({ type: "STT_ERROR", error: sttErr });
      }
    },
    stop() {
      if (destroyed) return;
      const state = machine.getState();
      if (state.status !== "recording") return;
      sttEventsActive = false;
      sttAdapter.stop();
      machine.dispatch({ type: "CANCEL" });
    },
    cancel() {
      if (destroyed) return;
      const state = machine.getState();
      if (state.status === "recording") {
        sttEventsActive = false;
        sttAdapter.abort();
        machine.dispatch({ type: "CANCEL" });
      } else if (state.status === "processing") {
        endpointClient.abort();
        machine.dispatch({ type: "CANCEL" });
      } else if (state.status === "confirming") {
        machine.dispatch({ type: "CANCEL" });
      } else if (state.status === "error") {
        machine.dispatch({ type: "ACKNOWLEDGE_ERROR" });
      }
    },
    async confirm() {
      if (destroyed) return;
      const state = machine.getState();
      if (state.status !== "confirming") return;
      machine.dispatch({ type: "CONFIRM" });
    },
    /**
     * Replace the active schema. Valid only from idle state.
     * Validates the new schema synchronously; throws VoiceFormConfigError on failure.
     * Clears the injector's element cache.
     * This is the v2 canonical method. updateSchema() is a deprecated alias. (FR-110)
     *
     * @throws {VoiceFormError} INVALID_TRANSITION if not in idle state.
     * @throws {VoiceFormConfigError} SCHEMA_INVALID if schema is invalid.
     */
    setSchema(schema) {
      if (destroyed) return;
      const state = machine.getState();
      if (state.status !== "idle") {
        throw new VoiceFormErrorImpl(
          "INVALID_TRANSITION",
          "setSchema() can only be called from the idle state.",
          false
        );
      }
      currentSchema = validateSchema(schema);
      injector.clearCache();
    },
    /**
     * Returns the schema currently in use.
     * Useful for multi-step forms where the developer inspects which schema
     * is active at any point.
     */
    getSchema() {
      return currentSchema;
    },
    /**
     * @deprecated Use setSchema() instead. Will be removed in v3.
     *
     * Replace the active schema. This method now delegates to setSchema()
     * and emits a deprecation warning on each call. (FR-110 / LLD § 4.5)
     */
    updateSchema(schema) {
      console.warn(
        "[voice-form] updateSchema() is deprecated and will be removed in v3. Use setSchema() instead."
      );
      this.setSchema(schema);
    },
    /**
     * Correct the value of a single field while in confirming state.
     *
     * The value is passed through sanitizeFieldValue before being applied.
     * If sanitization produces an empty string from a non-empty input, the
     * call is a no-op and returns false. (FR-114 / LLD § 4.4)
     *
     * Produces a FIELD_CORRECTED event that replaces ConfirmationData immutably.
     * NEVER mutates state.confirmation in place (security review #1).
     *
     * @param fieldName  The FieldSchema.name of the field to correct.
     * @param value      The corrected string value from the user.
     * @returns true if the correction was applied, false if rejected.
     */
    correctField(fieldName, value) {
      if (destroyed) return false;
      const state = machine.getState();
      if (state.status !== "confirming") return false;
      const fieldDef = currentSchema.fields.find((f) => f.name === fieldName);
      const fieldType = fieldDef?.type ?? "text";
      const options = fieldDef?.options;
      let sanitizedValue;
      try {
        const result = sanitizeFieldValue(value, fieldType, options);
        sanitizedValue = typeof result.value === "string" ? result.value : String(result.value);
      } catch {
        return false;
      }
      if (sanitizedValue.trim() === "" && value.trim() !== "") {
        return false;
      }
      const existingField = state.confirmation.parsedFields[fieldName];
      const correctedField = {
        label: existingField?.label ?? fieldDef?.label ?? fieldName,
        value: sanitizedValue,
        ...existingField?.confidence !== void 0 ? { confidence: existingField.confidence } : {},
        ...existingField?.existingValue !== void 0 ? { existingValue: existingField.existingValue } : {},
        userCorrected: true,
        ...existingField?.value !== void 0 ? { originalValue: existingField.value } : {}
      };
      const newParsedFields = {
        ...state.confirmation.parsedFields,
        [fieldName]: correctedField
      };
      const newConfirmation = {
        ...state.confirmation,
        parsedFields: newParsedFields
      };
      machine.dispatch({ type: "FIELD_CORRECTED", confirmation: newConfirmation });
      return true;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      handlingTransition = false;
      sttEventsActive = false;
      sttAdapter.abort();
      endpointClient.abort();
      if (autoResetTimer !== null) {
        clearTimeout(autoResetTimer);
        autoResetTimer = null;
      }
      if (cooldownTimerId !== null) {
        clearTimeout(cooldownTimerId);
        cooldownTimerId = null;
      }
      cooldownActive = false;
      machine.destroy();
      injector.clearCache();
    },
    subscribe(listener) {
      return machine.subscribe((state) => {
        listener(state);
      });
    }
  };
}

// src/prompt-builder.ts
var VERSION = "0.0.0";

export { VERSION, VoiceFormConfigError, createVoiceForm, createWebSpeechAdapter, validateSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map